import NewDashboardLayout from "component/layout/NewDashboardLayout";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryState } from "nuqs";
import {
  cancelFinanceRequest,
  cancelWalkThrough,
  createFinancePay,
  createRepayLoan,
  deleteFinanceDraft,
  fetchAllFinancePay,
  fetchBusinessDocuments,
  fetchCreditGroups,
} from "api/credit-fetchers";
import useUserDetails from "hooks/useUserDetails";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { paymentTypeForPayload } from "constants/global";
import { toast } from "react-toastify";
import { Loans, loansList, Payments, paymentsList } from "constants/FinanceConstants";
import Loading from "component/loading";
import FinanceDetails from "./FinanceDetails";
import StartTourModal from "component/finance/modals/StartTourModal";
import { fetchUserProfile } from "api/fetchers";
import Swal from "sweetalert2";
import { unformat } from "@react-input/number-format";

const Finance = () => {
  const {
    businessId,
    currency,
    creditWalletBalance,
    walletBalance,
    creditWalletId,
    walletBallanceId,
    isBusinessApproved,
    walletBalance: currentBalance,
    user,
  } = useUserDetails();
  const [financeDetails, setFinanceDetails] = useState(null);
  const [open, setOpen] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [contractEmployer, setContractEmployer] = useState(null);
  const [makePaymentModal, setMakePaymentModal] = useState(false);
  const [freightDocument, setFreightDocument] = useState(null);
  const [transactionDocument, setTransactionDocument] = useState(null);
  const [otherDocument, setOtherDocument] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [purchaseOrderUrl, setPurchaseOrderUrl] = useState(null);
  const [showAddDetails, setShowAddDetails] = useState(false);
  const [bankContractEmployer, setBankContractEmployer] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [showConfirmDetails, setShowConfirmDetails] = useState(false);
  const [makeARepayment, setMakeARepayment] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [repaymentData, setRepaymentData] = useState(null);
  const [payFull, setPayFull] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reason, setReason] = useState("");
  const [showPinModal, setShowPinModal] = useState(false);
  const [onPinSuccess, setOnPinSuccess] = useState(null);
  const [pinError, setPinError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDownloading] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [startTour, setStartTour] = useState(false);
  const [network, setNetwork] = useState();
  const [uniqueWallet, setUniqueWallet] = useState();
  const [walletTouched, setWalletTouched] = useState(false);
  const [saveRecipient, setSaveRecipient] = useState(false);

  const defaultState = () => {
    setAmount("");
    setPaymentMethod("");
    setAccountNumber("");
    setAccountName("");
    setReason("")
    setSelectedInvoice(null);
    setContractEmployer(null);
    setFreightDocument(null);
    setTransactionDocument(null);
    setOtherDocument(null);
    setBankContractEmployer(null);
    setPurchaseOrderUrl(null);
    setInvoiceUrl(null);
    setSelectedBank(null);
    setShowOtp(false);
    setShowPinModal(false);
    setRepaymentData(null);
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useQueryState("activeTab", {
    defaultValue: Loans,
  });
  const isLoanActive = activeTab === Loans;
  const [activeSubTab, setActiveSubTab] = useQueryState("subTab", {
    defaultValue: isLoanActive ? "Active" : "pending",
  });
  const subListFilter = isLoanActive ? loansList : paymentsList;
  const { mutateAsync: handleCancelWalkThrough, isLoading: isCancelingWalkThroughLoading } =
    useMutation({
      mutationFn: () => cancelWalkThrough(),
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["fetchUserProfile"] });
      },
    });

  const { mutateAsync: handleCancelFinance, isLoading: isCancelingFinanceLoading } = useMutation({
    mutationFn: (id) =>
      cancelFinanceRequest({
        businessId,
        id,
        reason,
        type: activeTab === Payments ? "payment" : "finance",
      }),
    onSettled: (response) => {
      if (response?.error === false) {
        toast.success(response?.message);
        queryClient?.invalidateQueries([
          "fetchCreditGroups",
          activeSubTab,
          "fetchAllFinancePay",
        ]);
        setReason("");
        refetchGroupCredit();
        refetchAllFinancePayData();
        setShowDeleteModal(false);
        setFinanceDetails(null);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });
  const { data: businessDocList, isLoading: isLoadingBusinessDocsList } = useQuery({
    queryKey: ["fetchBusinessDocuments"],
    queryFn: () => fetchBusinessDocuments({ businessId }),
  });
  const {
    data: allFinancePayData,
    isLoading: isLoadingAllFinancePayData,
    refetch: refetchAllFinancePayData,
  } = useQuery({
    queryKey: ["fetchAllFinancePay", activeSubTab],
    queryFn: () =>
      fetchAllFinancePay({
        businessId,
        status: activeSubTab === "all" ? "" : activeSubTab,
      }),
  });
  const { data: profileData, isLoading: isLoadingProfileData } = useQuery({
    queryKey: ["fetchUserProfile"],
    queryFn: () => fetchUserProfile(),
  });
  const { mutateAsync: handleCreateFinancePayFunc } = useMutation({
    mutationFn: (data) => {
      return createFinancePay({ businessId, data });
    },
    onSettled: async (data) => {
      if (data?.response?.data?.statusCode === 400) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: data?.response?.data?.message || "Failed to create finance payment",
        });
        return;
      }
      toast.success(data?.message);
      queryClient.invalidateQueries([
        "fetchBusinessDocuments",
        "fetchAllFinancePay",
        activeSubTab,
        "fetchCreditGroups",
      ]);
      refetchAllFinancePayData();
      setActiveTab(Payments);
      defaultState();
    },
    onError: async (error) => {
      console.error("Error creating finance payment:", error);
      toast.error("Failed to create finance payment");
    },
  });

  const {
    data,
    isLoading: isLoadingFinanceGroup,
    refetch: refetchGroupCredit,
  } = useQuery({
    queryKey: ["fetchCreditGroups", activeSubTab, currency],
    queryFn: () =>
      fetchCreditGroups({
        businessId,
        currency,
        isDraft: activeSubTab === loansList[1].value,
      }),
  });
  const { mutateAsync: deleteFinance, isLoading: isDeletingFinance } = useMutation({
    mutationFn: (id) => deleteFinanceDraft({ businessId, id }),
    onSettled: (response) => {
      if (response?.error === false) {
        queryClient.invalidateQueries(["fetchCreditGroups", activeSubTab]);
        toast.success(response?.message);
      } else {
        toast.error(response?.response?.data?.message);
      }
      queryClient.invalidateQueries(["fetchCreditGroups", activeSubTab]);
      refetchAllFinancePayData();
    },
  });

  const { mutateAsync: handleRepayFinanceFunc, isLoading: repayFinanceLoading } = useMutation({
    mutationFn: (data) => {
      return createRepayLoan({ businessId, data, financeId: repaymentData?._id });
    },
    onSettled: async (data) => {
      if (data?.response?.data?.statusCode === 400) {
        toast.error(data?.response?.data?.message || "Failed to create finance payment");
        return;
      }
      toast.success(data?.message);
      queryClient.invalidateQueries([
        "fetchBusinessDocuments",
        "fetchAllFinancePay",
        activeSubTab,
        "fetchCreditGroups",
      ]);
      defaultState();
      refetchAllFinancePayData();
    },
    onError: async (error) => {
      console.error("Error creating finance payment:", error);
      toast.error("Failed to create finance payment");
    },
  });

  useEffect(() => {
    if (!isLoadingProfileData && !profileData?.isViewFinanceWorkthrough) {
      if (localStorage.getItem("financeTourSkipped")) {
        return setStartTour(false);
      } else {
        return setStartTour(true);
      }
    }
  }, [isLoadingProfileData]);

  const docList = businessDocList?.data;
  const financeGroupList = data?.data ?? {};
  const IsEmptyFinanceGroupList = Object?.values(financeGroupList)?.every(
    (item) => !item?.length
  );
  const allFinancePayList = allFinancePayData?.data;
  const isCrypto = currency === 'USDT' || currency === 'USDC';

  const handleCreateFinancePay = async (otpCode) => {
    const payload = {
      paymentType: paymentTypeForPayload[paymentMethod],
      amount: Number(unformat(amount)),
      currency,
      pin: otpCode,
    };
    if (isCrypto) {
      payload.address = uniqueWallet;
      payload.network = network;
    } else {
      payload.bankName = selectedBank?.label
      payload.accountNumber = accountNumber
      payload.accountName = accountName
      payload.firstName = bankContractEmployer?.firstName
      payload.lastName = bankContractEmployer?.lastName
      payload.phoneNumber = bankContractEmployer?.phoneNumber
      payload.accountCode = selectedBank?.value
      payload.email = bankContractEmployer?.email
    }
    if (paymentMethod === "Invoice" && selectedInvoice?._id) {
      payload.invoiceId = selectedInvoice?._id;
    }
    if (paymentMethod === "Purchase order" && selectedInvoice?._id) {
      payload.purchaseOrderId = selectedInvoice?._id;
    }
    if (paymentMethod === "Purchase order" && purchaseOrderUrl?.url) {
      payload.url = purchaseOrderUrl?.url;
    }
    if (paymentMethod === "Invoice" && invoiceUrl?.url) {
      payload.url = invoiceUrl?.url;
    }
    if (paymentMethod === "Freight" && freightDocument?.url) {
      payload.url = freightDocument?.url;
    }
    if (paymentMethod === "Transaction" && transactionDocument?.url) {
      payload.url = transactionDocument?.url;
    }
    if (otherDocument?.url) {
      payload.url = otherDocument?.url;
    }
    if (reason) {
      payload.otherPaymentType = reason;
    }
    if (saveRecipient) {
      payload.addBeneficiary = saveRecipient;
    }
    try {
      await handleCreateFinancePayFunc(payload);
    } catch (error) {
      toast.error(error?.message || "Failed to create finance payment");
    }
  };
  const handleMakePayModal = () => setMakePaymentModal(true);
  const handleMakeRepaymentModal = (data) => {
    setRepaymentData(data);
    setAmount(data?.loans?.amount);
    setMakeARepayment(true);
  };
  const handleGenerateStatement = () => setShowGenerateModal(true);

  return (
    <NewDashboardLayout>
      {(isDeletingFinance || isLoadingProfileData) && (
        <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
          <Loading />
        </div>
      )}
      <FinanceDetails
        data={{
          saveRecipient, setSaveRecipient,
          activeTab,
          setActiveTab,
          setActiveSubTab,
          subListFilter,
          activeSubTab,
          isLoadingAllFinancePayData,
          isLoadingFinanceGroup,
          isCancelingFinanceLoading,
          IsEmptyFinanceGroupList,
          financeGroupList,
          currency,
          navigate,
          handleCancelFinance,
          handleMakePayModal,
          handleMakeRepaymentModal,
          allFinancePayList,
          setFinanceDetails,
          deleteFinance,
          open,
          setOpen,
          isBusinessApproved,
          businessId,
          walletBalance,
          creditWalletBalance,
          creditWalletId,
          handleGenerateStatement,
          setShowDeleteModal,
          isLoadingBusinessDocsList,
          docList,
          financeDetails,
          showGenerateModal,
          setShowGenerateModal,
          currentBalance,
          user,
          setOnPinSuccess,
          onPinSuccess,
          setShowPinModal,
          showPinModal,
          isDownloading,
          pinError,
          setPinError,
          makePaymentModal,
          setMakePaymentModal,
          contractEmployer,
          setContractEmployer,
          selectedInvoice,
          setSelectedInvoice,
          paymentMethod,
          setPaymentMethod,
          amount,
          setAmount,
          transactionDocument,
          setTransactionDocument,
          freightDocument,
          setFreightDocument,
          otherDocument,
          setOtherDocument,
          invoiceUrl,
          setInvoiceUrl,
          purchaseOrderUrl,
          setPurchaseOrderUrl,
          makeARepayment,
          setMakeARepayment,
          repaymentData,
          repayFinanceLoading,
          handleRepayFinanceFunc,
          showOtp,
          setShowOtp,
          paymentType,
          setShowSuccessModal,
          generatedData,
          setGeneratedData,
          setPaymentType,
          setPayFull,
          setShowAddDetails,
          payFull,
          accountName,
          setAccountName,
          accountNumber,
          setAccountNumber,
          selectedBank,
          setSelectedBank,
          showAddDetails,
          defaultState,
          bankContractEmployer,
          setBankContractEmployer,
          walletBallanceId,
          handleCreateFinancePay,
          showConfirmDetails,
          setShowConfirmDetails,
          showSuccessModal,
          reason,
          setReason,
          showDeleteModal,
          network,
          setNetwork,
          uniqueWallet,
          setUniqueWallet,
          walletTouched,
          setWalletTouched,
        }}
      />
      <StartTourModal
        show={startTour}
        onHide={() => setStartTour(false)}
        handleCancelWalkThrough={handleCancelWalkThrough}
        isCancelingWalkThroughLoading={isCancelingWalkThroughLoading}
      />
    </NewDashboardLayout>
  );
};

export default Finance;
